import React, { type ReactNode } from 'react';
import { Count, Root } from './TagPill.styled';

export interface TagPillProps {
  /** The tag label (the `#` prefix is added automatically when `prefix` is true). */
  label: string;
  /**
   * When omitted the pill renders as a non-interactive `<span>` (use this for
   * inline meta — e.g. row tag list). Provide `onClick` to render as a
   * `<button>` and pick up keyboard semantics.
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** Pin the active treatment (primary tint, used for active filters). */
  isActive?: boolean;
  /** Optional count badge appended after the label. */
  count?: number;
  /** Optional leading icon (e.g. a `check` glyph when active in tag-grid). */
  icon?: ReactNode;
  /** Prepend a `#` before the label. Defaults to `true`. */
  prefix?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  ariaLabel?: string;
  dataTestId?: string;
}

const TagPill: React.FC<TagPillProps> = ({
  label,
  onClick,
  isActive = false,
  count,
  icon,
  prefix = true,
  size = 'md',
  className,
  ariaLabel,
  dataTestId = 'tag-pill',
}) => {
  const isInteractive = !!onClick;
  const display = prefix ? `#${label}` : label;
  const commonProps = {
    className,
    'data-testid': dataTestId,
    'data-active': isActive,
  };

  if (isInteractive) {
    return (
      <Root
        as="button"
        type="button"
        $isActive={isActive}
        $isInteractive
        $size={size}
        aria-pressed={isActive}
        aria-label={ariaLabel ?? `Toggle tag ${label}`}
        onClick={onClick}
        {...commonProps}
      >
        {icon && (
          <span aria-hidden="true" data-testid={`${dataTestId}__icon`}>
            {icon}
          </span>
        )}
        <span>{display}</span>
        {typeof count === 'number' && (
          <Count $isActive={isActive} data-testid={`${dataTestId}__count`}>
            {count}
          </Count>
        )}
      </Root>
    );
  }

  return (
    <Root $isActive={isActive} $size={size} {...commonProps}>
      {icon && (
        <span aria-hidden="true" data-testid={`${dataTestId}__icon`}>
          {icon}
        </span>
      )}
      <span>{display}</span>
      {typeof count === 'number' && (
        <Count $isActive={isActive} data-testid={`${dataTestId}__count`}>
          {count}
        </Count>
      )}
    </Root>
  );
};

export default TagPill;
